import { default as AbstractScene } from './AbstractScene'
import { CUSTOM_EVENT, VARS } from '../config'
import * as THREE from 'three'
import getShader from '../shaders'
import { ENV } from '../config'
// import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { gsap } from '../gsap'
import { $html } from '../utils/dom'

// Dynamic imports for development mode only
let Tweakpane, TweakpaneEssentials
(async () => {
    if (ENV.IS_DEV) {
        Tweakpane = await import('tweakpane')
        TweakpaneEssentials = await import('@tweakpane/plugin-essentials')
    }
})()

const vertexShader = getShader('hypermeshVertex')
const fragmentShader = getShader('hypermeshFragment')

export default class SceneHypermesh extends AbstractScene {

    constructor(m) {
        super(m)

        this.SIZE = this.getData('size') || 1.3
        this.DPR = Math.max(window.devicePixelRatio, 2)

        this.settings = {
            meshQty: 48,
            meshSpacing: 0.02,
            meshSpin: 0.02,
            meshScrollSpin: -Math.PI,
            meshAutoRotate: {
                x: 0.001,
                y: 0.001,
                z: 0.000
            },
            camera: {
                origin: {
                    x: 0,
                    y: 0,
                    z: 2
                },
                target: {
                    x: 0.8,
                    y: 1,
                    z: 0
                },
                mouseSensitivity: {
                    x: -0.0001,
                    y: -0.0001
                }
            },
            color: $html.dataset.theme === 'light' ? '#000000' : '#ffffff',
            opacity: $html.dataset.theme === 'dark' ? 0.5 : 0.2,
        }
    }

    onInit() {

        this.uniforms = {
            u_time: { value: 0.0 },
            u_resolution: {
                value: {
                    x: this.sizes.width * this.DPR,
                    y: this.sizes.height * this.DPR
                }
            },

            u_color: new THREE.Uniform(new THREE.Color(this.settings.color)),
            u_noise_master_amp: { value: 0.0 },

            // wave 1
            u_noise_freq_1: { value: 1.17 },
            u_noise_amp_1: { value: 0.09 },
            u_spd_modifier_1: { value: 0.22 },
            // wave 2
            u_noise_freq_2: { value: 0.87 },
            u_noise_amp_2: { value: 0.22 },
            u_spd_modifier_2: { value: 0.43 },
            u_opacity: { value: this.settings.opacity }
        }

        this.initCamera()
        this.initObjects()
        this.initScrollTimeline()
        this.handleEvents()

        ENV.IS_DEV && this.initTweakpane()
    }

    initObjects() {
        this.axesHelper = new THREE.AxesHelper(1)
        this.axesHelper.visible = false
        this.scene.add(this.axesHelper)

        this.geometry = new THREE.EdgesGeometry(
            new THREE.PlaneGeometry(this.SIZE, this.SIZE, this.SIZE, this.SIZE)
        )

        this.material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader,
            fragmentShader,
            transparent: true
        })

        this.meshesOuter = new THREE.Group()
        this.meshesInner = new THREE.Group()

        this.populate()

        this.meshesOuter.add(this.meshesInner)
        this.scene.add(this.meshesOuter)
    }

    populate() {
        this.meshesInner.clear()

        this.meshes = []
        for (let i = 0; i < this.settings.meshQty; i++) {
            const mesh = new THREE.LineSegments(this.geometry, this.material)
            this.meshes.push(mesh)
            this.meshesInner.add(mesh)
        }

        this.meshesInner.position.z = this.meshes.length * -this.settings.meshSpacing / 2
    }

    initIntroTimeline() {
        this.introTl?.kill?.()

        this.introTl = gsap.timeline({
            defaults: {
                duration: 2,
                ease: 'power3.inOut',
            }
        })

        for (let i = 0; i < this.meshes.length; i++) {
            const mesh = this.meshes[i]

            this.introTl
                .set(mesh, {
                    visible: true
                }, 0)
                .to(mesh.position, {
                    z: `${i * this.settings.meshSpacing}`
                }, 0)
                .to(mesh.rotation, {
                    z: `${i * this.settings.meshSpin}`
                }, 0)
        }

        this.introTl.to(this.uniforms.u_noise_master_amp, {
            value: 1.0,
            ease: 'power4.inOut',
            duration: 4
        }, 0)
    }

    initScrollTimeline() {
        this.scrollTl?.progress?.(0)
        this.scrollTl?.kill?.()

        this.scrollTl = gsap.timeline({
            defaults: {
                duration: 2,
                ease: 'power2.out',
            },
            paused: true,
            onUpdate: () => {
                this.camera?.lookAt?.(0,0,0)
            }
        })
        .fromTo(this.camera.position, this.settings.camera.origin, this.settings.camera.target, 0)
        .to(this.meshesInner.rotation, {
            z: this.settings.meshScrollSpin
        }, 0)
    }

    initCamera() {
        const {x,y,z} = this.settings.camera.origin
        this.camera.position.set(x,y,z)

        // Camera rotation pivot
        this.orbit = new THREE.Object3D()
        this.orbit.rotation.order = 'YXZ'
        this.orbit.position.set(0,0,0)
        this.scene.add(this.orbit)
        this.orbit.add(this.camera)
    }

    initTweakpane() {
        if(!Tweakpane) {
            throw 'Tweakpane not defined'
            return
        }

        this.pane = new Tweakpane.Pane()
        this.pane.registerPlugin(TweakpaneEssentials)

        this.pane.hidden = true
        this.pane.containerElem_.style.position = 'fixed'
        this.pane.containerElem_.style.zIndex = 9999

        document.addEventListener('keyup', (e) => {
            if(e.key === 'Escape') {
                this.pane.hidden = !this.pane.hidden
                this.axesHelper.visible = !this.pane.hidden
            }

                /*
            if(e.key === 's') {
                const w = window.open('', '')
                w.document.title = 'Screenshot Hypermesh'
                const img = new Image()
                this.renderer.render(this.scene, this.camera)
                img.src = this.renderer.domElement.toDataURL('image/png')
                w.document.body.appendChild(img)
            }
            */
        })

        const debugPane = this.pane.addFolder({ title: 'Debug' })
        this.fpsGraph = debugPane.addBlade({
            view: 'fpsgraph',
            label: 'fpsgraph',
            lineCount: 2,
        })
        debugPane.addInput(this.axesHelper, 'visible', { label: 'Axes Helper' })

        const meshes = this.pane.addFolder({ title: 'Meshes' })
        meshes.addInput(this.settings, 'meshQty', { label: 'Qty', min: 0, max: 400, step: 1 }).on('change', this.reset.bind(this))
        meshes.addInput(this.settings, 'meshSpacing', { label: 'Spacing', min: 0, max: 0.04, step: 0.0001 }).on('change', this.reset.bind(this))
        meshes.addInput(this.settings, 'meshSpin', { label: 'Spin', min: -0.04, max: 0.04, step: 0.0001 }).on('change', this.reset.bind(this))
        meshes.addInput(this.settings, 'meshScrollSpin', { label: 'Scroll Spin', min: -Math.PI*2, max: Math.PI*2 }).on('change', this.reset.bind(this))
        meshes.addInput(this.settings, 'meshAutoRotate', { label: 'Auto Rotate', z: { step: 0.001 } })

        const camera = this.pane.addFolder({ title: 'Camera'})
        camera.addInput(this.camera, 'position', { disabled: true } )
        camera.addInput(this.settings.camera, 'origin').on('change', this.reset.bind(this))
        camera.addInput(this.settings.camera, 'target').on('change', this.reset.bind(this))
        camera.addInput(this.settings.camera, 'mouseSensitivity', { x: { min: -0.001, max: 0 }, y: { min: -0.001, max: 0 } }).on('change', this.reset.bind(this))

        const uniforms = this.pane.addFolder({ title: 'Uniforms' })
        uniforms.addInput(this.uniforms.u_color, 'value', { label: 'Col', color: {type: 'float'} })
        uniforms.addInput(this.uniforms.u_opacity, 'value', { label: 'Opacity', min: 0, max: 1 })
        uniforms.addInput(this.uniforms.u_noise_master_amp, 'value', { label: 'Noise master', min: 0, max: 1 })

        const noise1 = uniforms.addFolder({ title: 'Noise 1', expanded: false })
        noise1.addInput(this.uniforms.u_noise_freq_1, 'value', { label: 'Freq', min: 0, max: 4 })
        noise1.addInput(this.uniforms.u_noise_amp_1, 'value', { label: 'Amp', min: 0, max: 4 })
        noise1.addInput(this.uniforms.u_spd_modifier_1, 'value', { label: 'Speed', min: 0, max: 2 })

        const noise2 = uniforms.addFolder({ title: 'Noise 2', expanded: false })
        noise2.addInput(this.uniforms.u_noise_freq_2, 'value', { label: 'Freq', min: 0, max: 4 })
        noise2.addInput(this.uniforms.u_noise_amp_2, 'value', { label: 'Amp', min: 0, max: 4 })
        noise2.addInput(this.uniforms.u_spd_modifier_2, 'value', { label: 'Speed', min: 0, max: 2 })

        const actions = this.pane.addFolder({ title: 'Actions' })
        actions.addButton({ title: 'Reset' }).on('click', this.reset.bind(this))
        actions.addButton({ title: 'Toggle Noise' }).on('click', () => {
            gsap.to(this.uniforms.u_noise_master_amp, {
                value: Math.floor(1-this.uniforms.u_noise_master_amp.value)
            })
        })
    }

    updateTweakPane() {
        this.pane?.refresh?.()
    }

    reset() {
        this.call('scrollTo', { target: 0 }, 'Scroll')

        const {x,y,z} = this.settings.camera.origin
        this.camera.position.set(x,y,z)
        this.camera.lookAt(0,0,0)
        this.meshesInner.rotation.set(0,0,0)
        this.meshesOuter.rotation.set(0,0,0)

        this.populate()

        for(let mesh of this.meshes) {
            gsap.set(
                [mesh.position, mesh.rotation],
                {
                    x: 0, y: 0, z: 0
                },
                0
            )
        }
        gsap.set(
            this.uniforms.u_noise_master_amp, {
                value: 0
            }
        )

        this.initIntroTimeline()
        this.initScrollTimeline()
    }

    handleEvents() {
        // Window events
        window.addEventListener(CUSTOM_EVENT.READY, this.onReady = () => this.initIntroTimeline())
        if(ENV.IS_DESKTOP) {
            window.addEventListener('mousemove', this.onMouseMove = e => this.mouseMove(e))
        }
    }

    onResize() {
        // Update sizes
        this.sizes.width = this.$el.offsetWidth
        this.sizes.height = this.$el.offsetHeight

        if(this.uniforms) {
            this.uniforms.u_resolution.value.x = this.sizes.width * this.DPR
            this.uniforms.u_resolution.value.y = this.sizes.height * this.DPR
        }

        // Responsive object size (fix with camera Z)
        this.settings.camera.origin.z = gsap.utils.mapRange(500, 1500, 2 + 0.5 * this.SIZE, 2, this.sizes.width)
    }

    onRender(delta) {
        this.fpsGraph?.end?.()

        this.uniforms.u_time.value += delta

        // Auto rotate
        this.meshesOuter.rotation.x+=this.settings.meshAutoRotate.x
        this.meshesOuter.rotation.y+=this.settings.meshAutoRotate.y
        this.meshesOuter.rotation.z+=this.settings.meshAutoRotate.z

        ENV.IS_DEV && this.updateTweakPane()

        this.fpsGraph?.begin?.()
    }

    mouseMove(e) {
        this.orbit.rotateY(e.movementX * this.settings.camera.mouseSensitivity.y)
        this.orbit.rotateX(e.movementY * this.settings.camera.mouseSensitivity.x)
        this.orbit.rotation.z = 0
    }

    onScrollProgress(progress = 0) {
        // if(this.isDisabled) {
        //     return
        // }

        this.scrollTl?.progress?.(progress)
    }

    hide() {
        gsap.set(this.$el, {
            opacity: 0
        })
    }

    show() {
        gsap.to(this.$el, {
            opacity: 1,
            duration: 1,
            ease: 'power2.out',
        })
    }

    destroy(m) {
        super.destroy()

        // if(this.isDisabled) {
            // Window events
            window.removeEventListener(CUSTOM_EVENT.READY, this.onReady)
            if(ENV.IS_DESKTOP) {
                window.removeEventListener('mousemove', this.onMouseMove)
            }
        // }
    }
}
